import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Panel, Pill } from "@/components/admin/AdminUI";
import ConfirmDialog from "@/components/admin/ConfirmDialog";
import VersionHistory from "@/components/admin/VersionHistory";
import ContentBody from "@/components/site/ContentBody";
import { api } from "@/lib/api";
import { CONTENT_FORMAT_HELP } from "@/lib/constants";
import { formatDate } from "@/lib/format";

export default function ContentEditor({ meta, live, onSaved }) {
  const [title, setTitle] = useState(live?.title || meta.label);
  const [body, setBody] = useState(live?.body || "");
  const [reaccept, setReaccept] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);
  const [saveError, setSaveError] = useState(null);
  const dirty = body !== (live?.body || "") || title !== (live?.title || meta.label) || reaccept;
  const short = !["terms", "privacy", "safety", "chat_safety_tips", "faq"].includes(meta.key);

  const save = async () => {
    setSaveError(null);
    let res;
    try {
      res = await api(`/admin/content/${meta.key}`, { body: { title: title.trim() || null, body, require_reaccept: !!meta.reaccept && reaccept } });
    } catch (e) {
      if (e.status !== 422) throw e;
      setSaveError(e.message); // shown inline under the editor; the dialog closes
      return;
    }
    onSaved(res.content);
    setReaccept(false);
    setHistoryKey((k) => k + 1);
    toast.success(`Saved ${meta.label} as version ${res.content.version}.`);
  };

  return (
    <div className="min-w-0 space-y-6">
      <Panel className="p-6" data-testid="content-editor">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold" data-testid="content-editor-title">{meta.label}</h2>
          <span className="cw-muted text-xs" data-testid="content-live-version">Live: v{live?.version ?? "—"} · {live?.created_by || "—"} · {formatDate(live?.created_at)}</span>
        </div>
        <div className="mt-5"><Label htmlFor="ce-title">Title</Label>
          <Input id="ce-title" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-2" data-testid="content-title-input" /></div>
        <Tabs defaultValue="write" className="mt-5">
          <TabsList><TabsTrigger value="write" data-testid="content-tab-write">Write</TabsTrigger>{!meta.noPreview && <TabsTrigger value="preview" data-testid="content-tab-preview">Preview</TabsTrigger>}</TabsList>
          <TabsContent value="write">
            <Label htmlFor="ce-body" className="sr-only">Body</Label>
            <p className="cw-muted mt-2 text-xs leading-5" data-testid="content-format-help">{CONTENT_FORMAT_HELP}</p>
            {meta.help && <p className="cw-soft mt-2 text-sm leading-6" data-testid="content-help-text">{meta.help}</p>}
            {meta.rule && <p className="cw-brand-deep mt-1 text-sm font-medium leading-6" data-testid="content-rule-text">{meta.rule}</p>}
            <Textarea id="ce-body" value={body} onChange={(e) => { setBody(e.target.value); setSaveError(null); }} rows={meta.rows || (short ? 4 : 18)}
              aria-invalid={!!saveError} aria-describedby={saveError ? "ce-error" : undefined}
              className={`mt-2 text-sm leading-6 ${meta.noPreview ? "font-mono" : ""} ${saveError ? "border-[var(--cw-error)]" : ""}`} data-testid="content-body-input" />
            {saveError && <p id="ce-error" className="cw-error mt-2 text-sm" role="alert" data-testid="content-save-error">{saveError}</p>}
            {!short && <p className="cw-muted mt-2 text-xs">Blank line = new paragraph. Supports # headings, - bullets, 1. lists and **bold**.{meta.key === "faq" ? " For FAQ, put each question on its own line ending with “?”, answer below." : ""}</p>}
          </TabsContent>
          <TabsContent value="preview"><div className="mt-2 rounded-xl border border-border p-6" data-testid="content-preview"><ContentBody body={body} testId="content-preview-body" /></div></TabsContent>
        </Tabs>
        {meta.reaccept && (
          <label htmlFor="ce-reaccept" className="mt-5 flex cursor-pointer items-start gap-3 rounded-xl bg-[var(--cw-surface-tertiary)] p-4">
            <Checkbox id="ce-reaccept" checked={reaccept} onCheckedChange={(v) => setReaccept(!!v)} className="mt-0.5" data-testid="content-reaccept-checkbox" />
            <span><span className="block text-sm font-medium">Require users to re-accept</span>
              <span className="cw-soft block text-xs leading-5">Everyone is asked to accept this version the next time they open the app.</span></span>
          </label>
        )}
        <div className="mt-6 flex items-center justify-end gap-3">
          {dirty && <Pill tone="brand" data-testid="content-unsaved-pill">Unsaved</Pill>}
          <Button onClick={() => setConfirm(true)} disabled={!dirty || !body.trim()} data-testid="content-save-button">Save as new version</Button>
        </div>
      </Panel>
      <VersionHistory doc={meta.key} refreshKey={historyKey} onLoad={(v) => { setTitle(v.title); setBody(v.body); toast.message(`Loaded v${v.version} into the editor. Save to publish it.`); }} />
      <ConfirmDialog open={confirm} onOpenChange={setConfirm} destructive={false} testId="content-save-confirm-dialog"
        title={`Publish ${meta.label} v${(live?.version || 0) + 1}?`} confirmLabel="Publish" onConfirm={save}
        description={reaccept ? "It goes live now, and everyone will be asked to accept it again." : "It goes live on the website and in the app right away."} />
    </div>
  );
}
